/**
 * プロンプト管理画面
 * 一覧表示 + 編集・削除
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPrompts, getPrompt, updatePrompt, deletePrompt,
} from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Edit, Search, Loader2, Trash2, ChevronDown } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  extractBracePlaceholders,
  applyBracePreview,
  BRACE_PLACEHOLDER_HINTS,
  defaultSampleForPlaceholder,
  hasSectionBracketMarkers,
} from '../../lib/prompt-template-helpers';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';

const EMPTY_FORM = {
  name: '',
  prompt_type: '',
  prompt_text: '',
  description: '',
  is_active: true,
};

/** コードで実際に使われている prompt_type 一覧 */
const ACTIVE_PROMPT_TYPES = [
  { value: 'patient_system', label: '患者・会話システム' },
  { value: 'dentist_acknowledgement', label: '歯科医 相槌' },
  { value: 'dentist_category_prompt', label: '歯科医 カテゴリ質問' },
  { value: 'dentist_coaching', label: '歯科医 コーチング' },
  { value: 'dentist_coaching_chief_complaint', label: '歯科医 コーチング(主訴)' },
  { value: 'dentist_coaching_intake', label: '歯科医 コーチング(問診)' },
  { value: 'dentist_summary', label: '歯科医 セッション要約' },
  { value: 'summary_patient', label: '総評 患者面接' },
  { value: 'summary_patient_monshin', label: '総評 患者問診(初学者)' },
  { value: 'summary_dentist', label: '総評 歯科医報告' },
  { value: 'scoring_batch', label: 'バッチスコアリング' },
  { value: 'scoring_dentist_report', label: '歯科医報告スコアリング' },
  { value: 'communication_skill', label: 'C0コミュニケーション分析' },
  { value: 'item_extraction', label: 'ルーブリック項目抽出' },
  { value: 'applicability_analysis', label: '適用可能性分析' },
  { value: 'questionnaire_generation', label: '問診票生成' },
  { value: 'instruction_generation', label: '課題文生成' },
];

/** 一覧のカテゴリ別グループ（prompt_type ごと） */
const PROMPT_LIST_GROUPS: {
  id: string;
  title: string;
  description: string;
  types: readonly string[];
}[] = [
  {
    id: 'document',
    title: 'ドキュメント生成',
    description: '管理画面から問診票・課題文を LLM 生成するとき',
    types: ['questionnaire_generation', 'instruction_generation'],
  },
  {
    id: 'interview',
    title: '面接',
    description: 'WebSocket 面接セッション（歯科医ダイアログ・抽出など）。患者・会話システムはアバター別に別管理のため一覧では除外',
    types: [
      'applicability_analysis',
      'dentist_acknowledgement',
      'dentist_category_prompt',
      'dentist_coaching',
      'dentist_coaching_chief_complaint',
      'dentist_coaching_intake',
      'dentist_summary',
      'communication_skill',
      'item_extraction',
    ],
  },
  {
    id: 'scoring',
    title: 'スコアリング',
    description: '面接終了後の評価・総評・スコアリング API',
    types: [
      'scoring_batch',
      'scoring_dentist_report',
      'summary_patient',
      'summary_patient_monshin',
      'summary_dentist',
    ],
  },
];

function getPromptListGroupId(promptType: string | null | undefined): string {
  const t = promptType || '';
  for (const g of PROMPT_LIST_GROUPS) {
    if (g.types.includes(t)) return g.id;
  }
  return 'other';
}

function typeLabel(promptType: string | null | undefined): string {
  if (!promptType) return '-';
  return ACTIVE_PROMPT_TYPES.find((x) => x.value === promptType)?.label ?? promptType;
}

/** フォーム「名前」欄: DB の name が prompt_type と同じ英字キーのときは日本語ラベルを表示 */
function nameFieldDisplayValue(name: string, promptType: string): string {
  if (!promptType) return name;
  if (name === promptType) {
    const label = typeLabel(promptType);
    if (label !== promptType) return label;
  }
  return name;
}

/** 一覧・グループ化ソート用（getPrompts の行の最小形） */
type PromptListRow = {
  prompt_type?: string | null;
  name?: string | null;
};

export default function Prompts() {
  const [search, setSearch] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  /** プレビュー用。キーごとに上書きしたサンプル（未設定は defaultSampleForPlaceholder） */
  const [previewSampleOverrides, setPreviewSampleOverrides] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ---- クエリ ----
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'prompts', search],
    queryFn: () =>
      getPrompts({
        search,
        /** アバター／条件ごとに大量の行があるため一覧では除外（従来の「使用中のみ」相当） */
        exclude_type: 'patient_system',
        page: 1,
        limit: 500,
      }),
  });

  const prompts = useMemo(() => data?.prompts ?? [], [data?.prompts]);
  const groupedBuckets = useMemo(() => {
    const buckets: Record<string, PromptListRow[]> = {};
    for (const g of PROMPT_LIST_GROUPS) buckets[g.id] = [];
    buckets.other = [];
    for (const p of prompts) {
      const gid = getPromptListGroupId(p.prompt_type);
      (buckets[gid] ??= []).push(p);
    }
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a: PromptListRow, b: PromptListRow) => {
        const ta = a.prompt_type || '';
        const tb = b.prompt_type || '';
        if (ta !== tb) return ta.localeCompare(tb);
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return buckets;
  }, [prompts]);

  const bracePlaceholders = useMemo(
    () => extractBracePlaceholders(formData.prompt_text),
    [formData.prompt_text],
  );

  const previewSamplesEffective = useMemo(() => {
    const m: Record<string, string> = {};
    for (const k of bracePlaceholders) {
      m[k] =
        previewSampleOverrides[k] !== undefined
          ? previewSampleOverrides[k]!
          : defaultSampleForPlaceholder(k);
    }
    return m;
  }, [bracePlaceholders, previewSampleOverrides]);

  const previewFilledText = useMemo(
    () => applyBracePreview(formData.prompt_text, previewSamplesEffective),
    [formData.prompt_text, previewSamplesEffective],
  );

  const showSectionBracketNote = useMemo(
    () => hasSectionBracketMarkers(formData.prompt_text),
    [formData.prompt_text],
  );

  // ---- Mutations ----
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updatePrompt(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
      setIsEditOpen(false);
      setSelectedPrompt(null);
      toast({ title: '成功', description: 'プロンプトを更新しました' });
    },
    onError: (e: Error) => {
      toast({ title: 'エラー', description: e.message || '更新に失敗しました', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
      setIsDeleteOpen(false);
      setSelectedPrompt(null);
      toast({ title: '成功', description: 'プロンプトを削除しました' });
    },
    onError: (e: Error) => {
      toast({ title: 'エラー', description: e.message || '削除に失敗しました', variant: 'destructive' });
    },
  });

  const renderPromptRow = (prompt: any) => (
    <TableRow key={prompt.id} className="border-b border-gray-100 hover:bg-gray-50/50">
      <TableCell className="py-4">
        <span className="text-base font-medium text-gray-900">{typeLabel(prompt.prompt_type)}</span>
      </TableCell>
      <TableCell className="py-4 text-base text-gray-500 max-w-[250px] truncate">
        {prompt.description || (prompt.prompt_text ? `${prompt.prompt_text.substring(0, 60)}...` : '')}
      </TableCell>
      <TableCell className="py-4">
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => handleEdit(prompt)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            aria-label="編集"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => { setSelectedPrompt(prompt); setIsDeleteOpen(true); }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
            aria-label="削除"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );

  // ---- ハンドラ ----
  const handleEdit = async (prompt: any) => {
    try {
      const full = await getPrompt(prompt.id);
      const p = full.prompt;
      setSelectedPrompt(p);
      setFormData({
        name: p.name || '',
        prompt_type: p.prompt_type || '',
        prompt_text: p.prompt_text || '',
        description: p.description || '',
        is_active: p.is_active !== false,
      });
      setPreviewSampleOverrides({});
      setIsEditOpen(true);
    } catch {
      toast({ title: 'エラー', description: '取得に失敗しました', variant: 'destructive' });
    }
  };

  const handleUpdate = () => {
    if (!selectedPrompt) return;
    updateMutation.mutate({
      id: selectedPrompt.id,
      data: {
        prompt_text: formData.prompt_text,
        is_active: formData.is_active,
      },
    });
  };

  const readOnlyFieldClass = 'bg-gray-50 text-gray-800 cursor-not-allowed';

  const setPreviewSampleForKey = useCallback((key: string, value: string) => {
    setPreviewSampleOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ---- 編集フォーム ----
  const renderEditForm = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="edit-name">名前</Label>
        <Input id="edit-name"
          readOnly
          className={readOnlyFieldClass}
          value={nameFieldDisplayValue(formData.name, formData.prompt_type)}
        />
      </div>
      <div>
        <Label htmlFor="edit-prompt_type">タイプ (prompt_type)</Label>
        <Input id="edit-prompt_type" readOnly className={readOnlyFieldClass}
          value={formData.prompt_type} />
        <p className="text-xs text-gray-400 mt-1">コードで getByType() に渡すキー（変更不可）</p>
      </div>
      <div>
        <Label htmlFor="edit-description">説明</Label>
        <Input id="edit-description" readOnly className={readOnlyFieldClass}
          value={formData.description} />
      </div>
      <div className="space-y-3">
        <div>
          <Label htmlFor="edit-prompt_text">プロンプトテキスト</Label>
          <p className="text-xs text-gray-500 mt-1">
            <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px]">{'{{変数名}}'}</code>
            {' '}は実行時にアプリが置換します。下の「変数」「プレビュー」は理解用の例です（保存内容は上の本文そのままです）。
          </p>
        </div>
        <textarea id="edit-prompt_text"
          className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
          rows={12}
          value={formData.prompt_text}
          onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
          placeholder="あなたは歯科医師です。..." />
        {showSectionBracketNote && (
          <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <span className="font-medium">【…】形式の見出し</span>
            が含まれています。一部のプロンプトでは、面接開始時などに
            <code className="mx-1 rounded bg-amber-100 px-1">【患者のプロフィール】</code>
            などへ長文が差し替えられます。これは{' '}
            <code className="rounded bg-amber-100 px-1">{'{{}}'}</code>
            {' '}プレビューとは別の処理です。
          </div>
        )}
        {(bracePlaceholders.length > 0 || showSectionBracketNote) && (
          <Collapsible defaultOpen className="border border-gray-200 rounded-md">
            <CollapsibleTrigger
              type="button"
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 [&[data-state=open]>svg]:rotate-180"
            >
              <span>変数（{'{{...}}'}）とプレビュー</span>
              <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 border-t border-gray-200 px-3 pb-3 pt-2">
              {bracePlaceholders.length > 0 ? (
                <>
                  <p className="text-xs text-gray-500">
                    検出されたプレースホルダです。サンプル列を編集すると、下のプレビューに反映されます。
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {bracePlaceholders.map((key) => {
                      const hint = BRACE_PLACEHOLDER_HINTS[key];
                      const sampleVal =
                        previewSampleOverrides[key] !== undefined
                          ? previewSampleOverrides[key]!
                          : defaultSampleForPlaceholder(key);
                      return (
                        <div key={key} className="rounded-md bg-gray-50 p-2 text-sm space-y-1">
                          <div className="font-mono text-xs text-medical-blue font-semibold">
                            {'{{'}{key}{'}}'}
                          </div>
                          {hint ? (
                            <div className="text-xs text-gray-600">
                              <span className="font-medium text-gray-800">{hint.label}</span>
                              {' — '}
                              {hint.description}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500">
                              実行時にコードから渡される値に置き換わります。意味はサーバー実装を参照してください。
                            </div>
                          )}
                          <div>
                            <Label className="text-xs text-gray-500">プレビュー用サンプル</Label>
                            <textarea
                              className="mt-1 w-full min-h-[56px] px-2 py-1.5 border border-gray-200 rounded font-mono text-xs"
                              value={sampleVal}
                              onChange={(e) => setPreviewSampleForKey(key, e.target.value)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">完成イメージ（プレビュー）</Label>
                    <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-slate-50 p-3 font-mono text-xs text-gray-800">
                      {previewFilledText}
                    </pre>
                  </div>
                </>
              ) : (
                !showSectionBracketNote && (
                  <p className="text-xs text-gray-500">{'{{...}}'} 形式の変数は検出されませんでした。</p>
                )
              )}
              {bracePlaceholders.length === 0 && showSectionBracketNote && (
                <p className="text-xs text-gray-500">
                  {'{{...}}'} はありません。上記の【…】注記を参照してください。
                </p>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
      <div className="flex items-center space-x-2">
        <input type="checkbox" id="edit-is_active" checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
        <Label htmlFor="edit-is_active">アクティブ</Label>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">プロンプト管理</h1>
          <p className="text-base text-gray-500 mt-2">システムプロンプトの編集</p>
        </div>

        {/* 検索 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex-1 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="名前で検索..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-11 h-11 rounded-xl border-gray-200 bg-white" />
          </div>
        </div>

        {/* 一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-2">プロンプト一覧</div>
          <p className="text-base text-gray-500 mb-4">
            「ドキュメント生成」「面接」「スコアリング」に分類して表示します（最大 500 件）。
            患者・会話システム（<code className="text-base bg-gray-100 px-1 rounded">patient_system</code>）はアバター等ごとにレコードが多数あるため、この一覧には含めていません。
          </p>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-base text-red-800">
              <p className="font-medium">プロンプト一覧を取得できませんでした</p>
              <p className="mt-1 text-red-700">
                {error instanceof Error ? error.message : '不明なエラー'}
              </p>
            </div>
          ) : (
            <>
              {prompts.length >= 500 && (
                <p className="text-base text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
                  表示は最大 500 件です。検索で絞り込んでください。
                </p>
              )}

              <div className="space-y-10">
                {PROMPT_LIST_GROUPS.map((g) => {
                  const rows = groupedBuckets[g.id] ?? [];
                  return (
                    <section key={g.id} className="space-y-3">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{g.title}</h2>
                        <p className="text-base text-gray-500">{g.description}</p>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-gray-200 hover:bg-transparent">
                            <TableHead className="text-base font-bold text-gray-700">タイプ</TableHead>
                            <TableHead className="text-base font-bold text-gray-700">説明</TableHead>
                            <TableHead className="text-base font-bold text-gray-700 text-right">操作</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.length === 0 ? (
                            <TableRow className="border-b border-gray-100">
                              <TableCell colSpan={3} className="text-base text-gray-500 py-6 text-center">
                                該当するプロンプトはありません
                              </TableCell>
                            </TableRow>
                          ) : (
                            rows.map((prompt: any) => renderPromptRow(prompt))
                          )}
                        </TableBody>
                      </Table>
                    </section>
                  );
                })}
                {(groupedBuckets.other?.length ?? 0) > 0 && (
                  <section className="space-y-3">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">その他</h2>
                      <p className="text-base text-gray-500">上記カテゴリに含まれない prompt_type</p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-gray-200 hover:bg-transparent">
                          <TableHead className="text-base font-bold text-gray-700">タイプ</TableHead>
                          <TableHead className="text-base font-bold text-gray-700">説明</TableHead>
                          <TableHead className="text-base font-bold text-gray-700 text-right">操作</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(groupedBuckets.other ?? []).map((prompt: any) => renderPromptRow(prompt))}
                      </TableBody>
                    </Table>
                  </section>
                )}
              </div>
            </>
          )}
        </div>

        {/* 編集ダイアログ */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>プロンプト編集</DialogTitle>
              <DialogDescription>ID: {selectedPrompt?.id}</DialogDescription>
            </DialogHeader>
            {renderEditForm()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>キャンセル</Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending || !formData.prompt_text}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認 */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>プロンプトを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。「{selectedPrompt?.name}」(ID: {selectedPrompt?.id}) を削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={() => selectedPrompt && deleteMutation.mutate(selectedPrompt.id)}
                disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
